'use client';

import { useState, useEffect } from 'react';
import { AreaChart, Area, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface SignalRecord {
  id: string;
  signal: string;
  source: string;
  value: number;
  quality: number;
  timestamp: string;
  game: string;
  stored: boolean;
}

const SIGNAL_STORE: SignalRecord[] = [
  { id: 'sig-001', signal: 'Goalie Pressure Score', source: 'ProprietaryEngine', value: 88, quality: 94, timestamp: '2026-05-16 22:41', game: 'BOS vs TOR', stored: true },
  { id: 'sig-002', signal: 'Travel Stress Index', source: 'FatigueAgent', value: 78, quality: 87, timestamp: '2026-05-16 22:38', game: 'TBL vs FLA', stored: true },
  { id: 'sig-003', signal: 'Lineup Chemistry', source: 'ProprietaryEngine', value: 91, quality: 96, timestamp: '2026-05-16 22:35', game: 'COL vs VGK', stored: true },
  { id: 'sig-004', signal: 'Momentum Persistence', source: 'MomentumAgent', value: 74, quality: 82, timestamp: '2026-05-16 22:30', game: 'BOS vs TOR', stored: true },
  { id: 'sig-005', signal: 'Clutch Performance', source: 'ProprietaryEngine', value: 85, quality: 91, timestamp: '2026-05-16 22:25', game: 'COL vs VGK', stored: true },
];

const QUALITY_DISTRIBUTION = [
  { name: 'Excellent (90+)', value: 34, color: '#10b981' },
  { name: 'Good (75-89)', value: 48, color: '#3b82f6' },
  { name: 'Fair (60-74)', value: 14, color: '#f59e0b' },
  { name: 'Poor (<60)', value: 4, color: '#ef4444' },
];

const AI_DECISION_HISTORY = [
  { id: 'dec-001', timestamp: '22:41', game: 'BOS vs TOR', decision: 'STRONG BOS', confidence: 91, outcome: 'PENDING', signals: 8 },
  { id: 'dec-002', timestamp: '21:15', game: 'COL vs VGK', decision: 'COL EDGE', confidence: 84, outcome: 'WIN', signals: 6 },
  { id: 'dec-003', timestamp: '20:30', game: 'TBL vs FLA', decision: 'MONITOR', confidence: 71, outcome: 'LOSS', signals: 5 },
  { id: 'dec-004', timestamp: '19:45', game: 'BOS vs MTL', decision: 'STRONG BOS', confidence: 88, outcome: 'WIN', signals: 9 },
  { id: 'dec-005', timestamp: '18:30', game: 'EDM vs CGY', decision: 'EDM EDGE', confidence: 79, outcome: 'WIN', signals: 7 },
];

const PLATFORM_METRICS = Array.from({ length: 30 }, (_, i) => ({
  day: `J-${30 - i}`,
  signalsStored: Math.floor(Math.random() * 200 + 400),
  qualityScore: 80 + Math.sin(i * 0.3) * 8 + Math.random() * 4,
  decisionAccuracy: 68 + Math.sin(i * 0.2) * 6 + i * 0.2 + Math.random() * 3,
}));

const ENRICHED_DATASETS = [
  { name: 'NHL Games 2024-26', records: '4,847', quality: 96, lastUpdate: '22:41', size: '2.3 GB' },
  { name: 'Player Performance', records: '128,492', quality: 94, lastUpdate: '22:38', size: '8.7 GB' },
  { name: 'Goalie Stats', records: '18,234', quality: 98, lastUpdate: '22:35', size: '1.1 GB' },
  { name: 'Market Odds History', records: '892,341', quality: 91, lastUpdate: '22:30', size: '45.2 GB' },
  { name: 'Proprietary Signals', records: '234,891', quality: 97, lastUpdate: '22:41', size: '12.8 GB' },
];

export default function DataMoatClient() {
  const [activeTab, setActiveTab] = useState<'signals' | 'decisions' | 'datasets' | 'platform'>('signals');
  const [totalSignals, setTotalSignals] = useState(234891);
  const [qualityScore, setQualityScore] = useState(94.2);

  useEffect(() => {
    const interval = setInterval(() => {
      setTotalSignals(prev => prev + Math.floor(Math.random() * 8 + 2));
      setQualityScore(prev => +(prev + (Math.random() - 0.5) * 0.2).toFixed(1));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="CircleStackIcon" size={20} className="text-teal-400" />
            <h1 className="text-xl font-bold text-foreground">Data Moat & Platform Intelligence</h1>
            <span className="px-2 py-0.5 text-2xs font-mono bg-teal-400/10 text-teal-400 border border-teal-400/20 rounded">PHASE QUANT · ÉTAPE 10</span>
          </div>
          <p className="text-sm text-muted-foreground">Infrastructure d&apos;intelligence propriétaire — signaux, datasets enrichis, historique IA</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">SIGNAUX STOCKÉS</div>
            <div className="text-lg font-bold text-teal-400 font-mono">{totalSignals.toLocaleString()}</div>
          </div>
          <div className="text-right">
            <div className="text-2xs text-muted-foreground font-mono">QUALITÉ DONNÉES</div>
            <div className="text-lg font-bold text-green-400 font-mono">{qualityScore}%</div>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Signaux Propriétaires', value: totalSignals.toLocaleString(), icon: 'LockClosedIcon', color: 'text-teal-400' },
          { label: 'Décisions IA Historisées', value: '18,492', icon: 'CpuChipIcon', color: 'text-purple-400' },
          { label: 'Score Qualité Données', value: `${qualityScore}%`, icon: 'ShieldCheckIcon', color: 'text-green-400' },
          { label: 'Datasets Enrichis', value: '5 actifs', icon: 'CircleStackIcon', color: 'text-blue-400' },
        ].map(kpi => (
          <div key={kpi.label} className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xs text-muted-foreground font-mono uppercase">{kpi.label}</span>
              <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={14} className={kpi.color} />
            </div>
            <div className={`text-xl font-bold font-mono ${kpi.color}`}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {(['signals', 'decisions', 'datasets', 'platform'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === tab ? 'text-teal-400 border-b-2 border-teal-400' : 'text-muted-foreground hover:text-foreground'}`}>
            {tab === 'signals' ? 'Signaux Propriétaires' : tab === 'decisions' ? 'Historique Décisions IA' : tab === 'datasets' ? 'Datasets Enrichis' : 'Analytics Plateforme'}
          </button>
        ))}
      </div>

      {activeTab === 'signals' && (
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Stockage Signaux Propriétaires</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-background/50">
                    {['Signal', 'Source', 'Valeur', 'Qualité', 'Match', 'Timestamp'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-2xs font-medium text-muted-foreground uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SIGNAL_STORE.map(sig => (
                    <tr key={sig.id} className="border-b border-border/50 hover:bg-background/30 transition-colors">
                      <td className="px-4 py-2.5 font-medium text-foreground">{sig.signal}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{sig.source}</td>
                      <td className="px-4 py-2.5 font-mono font-bold text-teal-400">{sig.value}</td>
                      <td className="px-4 py-2.5">
                        <span className={`font-mono text-xs ${sig.quality >= 90 ? 'text-green-400' : sig.quality >= 75 ? 'text-yellow-400' : 'text-red-400'}`}>{sig.quality}%</span>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{sig.game}</td>
                      <td className="px-4 py-2.5 font-mono text-muted-foreground">{sig.timestamp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Distribution Qualité</h3>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={QUALITY_DISTRIBUTION} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value">
                  {QUALITY_DISTRIBUTION.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {QUALITY_DISTRIBUTION.map(q => (
                <div key={q.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: q.color }} />
                    <span className="text-muted-foreground">{q.name}</span>
                  </div>
                  <span className="font-mono text-foreground">{q.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'decisions' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Historique Décisions IA</h3>
            <div className="flex gap-3 text-xs text-muted-foreground">
              <span>WIN: <span className="text-green-400 font-mono">3</span></span>
              <span>LOSS: <span className="text-red-400 font-mono">1</span></span>
              <span>PENDING: <span className="text-yellow-400 font-mono">1</span></span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  {['Timestamp', 'Match', 'Décision IA', 'Confiance', 'Signaux', 'Résultat'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-2xs font-medium text-muted-foreground uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {AI_DECISION_HISTORY.map(dec => (
                  <tr key={dec.id} className="border-b border-border/50 hover:bg-background/30 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-muted-foreground">{dec.timestamp}</td>
                    <td className="px-4 py-2.5 font-medium text-foreground">{dec.game}</td>
                    <td className="px-4 py-2.5 font-bold text-teal-400">{dec.decision}</td>
                    <td className="px-4 py-2.5 font-mono text-foreground">{dec.confidence}%</td>
                    <td className="px-4 py-2.5 font-mono text-muted-foreground">{dec.signals}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded border text-2xs font-mono ${dec.outcome === 'WIN' ? 'bg-green-400/10 border-green-400/30 text-green-400' : dec.outcome === 'LOSS' ? 'bg-red-400/10 border-red-400/30 text-red-400' : 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400'}`}>
                        {dec.outcome}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'datasets' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Datasets Enrichis</h3>
          </div>
          <div className="divide-y divide-border">
            {ENRICHED_DATASETS.map(ds => (
              <div key={ds.name} className="p-4 flex items-center justify-between hover:bg-background/30 transition-colors">
                <div className="flex items-center gap-3">
                  <Icon name="CircleStackIcon" size={16} className="text-teal-400" />
                  <div>
                    <div className="text-sm font-medium text-foreground">{ds.name}</div>
                    <div className="text-2xs text-muted-foreground font-mono">{ds.records} enregistrements · {ds.size}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-2xs text-muted-foreground">Qualité</div>
                    <div className={`text-sm font-bold font-mono ${ds.quality >= 95 ? 'text-green-400' : 'text-yellow-400'}`}>{ds.quality}%</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xs text-muted-foreground">Mis à jour</div>
                    <div className="text-xs font-mono text-foreground">{ds.lastUpdate}</div>
                  </div>
                  <span className="px-2 py-1 text-2xs bg-green-400/10 text-green-400 border border-green-400/20 rounded font-mono">ACTIF</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'platform' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Analytics Prédictifs Plateforme (30j)</h3>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={PLATFORM_METRICS}>
                <defs>
                  <linearGradient id="lgQual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 9 }} interval={4} />
                <YAxis tick={{ fill: '#64748b', fontSize: 9 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
                <Area type="monotone" dataKey="qualityScore" stroke="#14b8a6" fill="url(#lgQual)" strokeWidth={2} name="Qualité %" />
                <Line type="monotone" dataKey="decisionAccuracy" stroke="#a855f7" strokeWidth={1.5} dot={false} name="Précision Décisions %" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Performance Adaptative</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={PLATFORM_METRICS.slice(-10)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 9 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 9 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
                <Bar dataKey="signalsStored" fill="#14b8a6" radius={[4, 4, 0, 0]} name="Signaux Stockés" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
